// @flow
import last from 'lodash/fp/last';
import groupBy from 'lodash/fp/groupBy';
import pipe from 'lodash/fp/pipe';
import filter from 'lodash/fp/filter';
import sumBy from 'lodash/fp/sumBy';
import map from 'lodash/fp/map';

import type { Facet } from 'modules/facet';
import { isoDatePart, atFirstOfMonth, toDate, dateString } from 'modules/date';
import { type User, customers } from 'modules/user';

export type OrderHistoryQuery = $ReadOnly<{|
  accountNumber?: string,
  locationNumber?: string,
  erpOrderNumbers?: $ReadOnlyArray<string>,
  orderDateFrom?: Date,
  orderDateTo?: Date,
  shipDateFrom?: Date,
  shipDateTo?: Date,
  invoiceDateFrom?: Date,
  invoiceDateTo?: Date,
  updatedAtFrom?: Date,
  updatedAtTo?: Date,
  range?: string,
  state?: string,
  groupingColumns?: $ReadOnlyArray<string>,
  sortBy?:
    | 'invoice_date'
    | 'erp_order_number'
    | 'po'
    | 'invoice_number'
    | 'quantity'
    | 'amount'
    | 'backorder_date',
  sortDirection?: 'ASC' | 'DESC',
  filterQuery?: { [string]: string },
  customerNumbers?: $ReadOnlyArray<string>,
|}>;

export type OrderHistoryRow = {
  account_name: string,
  account_number: string,
  alt_size: string | null,
  amount: number,
  backorder_date: Date | string | null,
  cancel_date: Date | string,
  cash_discount: number | null,
  client_fields: any,
  complete_amount: number,
  complete_quantity: number,
  created_at: Date | string,
  currency_code: string,
  discount_percent: number | null,
  due_amount: number | null,
  elastic_order_number: null,
  erp_order_number: string,
  expected_ship_date: Date | string | null,
  invoice_date: string | null,
  invoice_number: string | null,
  key: string,
  line_discount: string | null,
  line_item_number: string,
  location_name: string,
  location_number: ?string,
  net_price: number,
  open_amount: number,
  open_quantity: number,
  order_date: Date | string,
  order_type: string,
  parcel_number: string | null,
  po: string,
  product_name: string,
  product_number: string,
  quantity: number,
  region: string | null,
  relevance: Date | string,
  rep_name: string | null,
  rma_number: string | null,
  season: string | null,
  ship_date: Date | string,
  size_name: string,
  state: string,
  state_amounts: number[],
  state_quantities: number[],
  states: string[],
  stock_item_key: string,
  tax_amount: number | null,
  terms_name: string | null,
  tracking_carrier: string | null,
  tracking_number: string | null,
  unit_price: number,
  updated_at: Date | string | null,
  variation_code: string,
  variation_name: string,
  working_amount: number,
  working_quantity: number,
  year: string | number | null,
};

// Type OrderHistoryRow vs Type OrderHistoryForDashboardWidget
// We need to introduce another type specifically for order history
// data for the dashboard widget. To obtain order history data for
// the dashboard widget we send a query to skillet
// different from the regular order
// history query, so it returns a different set of fields. Also the
// state field differs in type from the OrderHistoryRow state field.
export type MetadataOrderHistoryRow = $ReadOnly<{|
  _id: string,
  erp_order_number: string,
  order_date: string,
  account_number: string,
  account_name: string,
  ship_date: Date | string,
  relevance: Date | string,
  po: ?string,
  state: $ReadOnlyArray<string>,
|}>;

export type SerializedMetadataOrderHistoryResponse = $ReadOnly<{|
  body: $ReadOnlyArray<MetadataOrderHistoryRow>,
|}>;

export type OrderHistoryResponse = $ReadOnly<{|
  results: $ReadOnlyArray<OrderHistoryRow>,
  facets: $ReadOnlyArray<Facet>,
  total: number,
|}>;

export type SerializedOrderHistoryResponse = $ReadOnly<{|
  body: $ReadOnlyArray<OrderHistoryRow>,
  range: string,
|}>;

export const orderHistoryQueryAsString = ({
  accountNumber,
  locationNumber,
  orderDateFrom,
  orderDateTo,
  shipDateFrom,
  shipDateTo,
  invoiceDateFrom,
  invoiceDateTo,
  updatedAtFrom,
  updatedAtTo,
  state,
  range,
  sortBy,
  sortDirection,
  groupingColumns,
  erpOrderNumbers,
  filterQuery,
  customerNumbers,
}: OrderHistoryQuery): string => {
  const params = new URLSearchParams();
  if (accountNumber)
    params.append('filter_query[account_number]', accountNumber);
  if (locationNumber)
    params.append('filter_query[location_number]', locationNumber);
  if (orderDateFrom)
    params.append('order_date_from', isoDatePart(orderDateFrom));
  if (orderDateTo) params.append('order_date_to', isoDatePart(orderDateTo));
  if (shipDateFrom) params.append('ship_date_from', isoDatePart(shipDateFrom));
  if (shipDateTo) params.append('ship_date_to', isoDatePart(shipDateTo));
  if (invoiceDateFrom)
    params.append('invoice_date_from', isoDatePart(invoiceDateFrom));
  if (invoiceDateTo)
    params.append('invoice_date_to', isoDatePart(invoiceDateTo));
  if (updatedAtFrom)
    params.append('updated_at_from', isoDatePart(updatedAtFrom));
  if (updatedAtTo) params.append('updated_at_to', isoDatePart(updatedAtTo));
  if (range) params.append('Range', range);
  if (groupingColumns) {
    groupingColumns.map((grouping) =>
      params.append('grouping_columns[]', grouping),
    );
  }
  if (customerNumbers) {
    customerNumbers.map((customer) =>
      params.append('customer_numbers[]', customer),
    );
  }
  if (erpOrderNumbers) {
    params.append(
      'filter_query[erp_order_number:.in]',
      erpOrderNumbers.join('|'),
    );
  }
  if (state) {
    params.append('filter_query[state]', state);
  }
  if (filterQuery)
    Object.entries(filterQuery).forEach(([key, value]) => {
      // $FlowFixMe[incompatible-call]
      params.append(`filter_query[${key}:.match]`, value);
    });

  const sortDirectionSymbol = (sortDirection || 'DESC') === 'DESC' ? '-' : '+';

  const sortParam = sortBy
    ? `&${encodeURIComponent(`sort(${sortDirectionSymbol}${sortBy})`)}`
    : encodeURIComponent('');
  return `${params.toString()}${sortParam}`;
};

export const deserializedOrderHistoryResponse = ({
  body,
  range,
}: SerializedOrderHistoryResponse): OrderHistoryResponse => ({
  results: body,
  facets: [],
  total: Number(last(range.split('/'))),
});

export const groupedByLocationNumber = (
  rows: $ReadOnlyArray<OrderHistoryRow>,
): Map<string, $ReadOnlyArray<OrderHistoryRow>> =>
  pipe(
    filter(
      // eslint-disable-next-line camelcase
      ({ location_number }) =>
        // eslint-disable-next-line camelcase
        location_number !== null &&
        // eslint-disable-next-line camelcase
        typeof location_number !== 'undefined' &&
        // eslint-disable-next-line camelcase
        location_number !== '',
    ),
    groupBy('location_number'),
    Object.entries,
    (
      groupedEntries: any,
    ): $ReadOnlyArray<[string, $ReadOnlyArray<OrderHistoryRow>]> =>
      groupedEntries,
    (groupedEntries) => new Map(groupedEntries),
  )(rows);

export const groupedByInvoiceMonth = (
  rows: $ReadOnlyArray<OrderHistoryRow>,
): Map<Date, $ReadOnlyArray<OrderHistoryRow>> =>
  pipe(
    // eslint-disable-next-line camelcase
    filter(
      // eslint-disable-next-line camelcase
      ({ invoice_date }) =>
        // eslint-disable-next-line camelcase
        invoice_date !== null && typeof invoice_date !== 'undefined',
    ),
    // eslint-disable-next-line camelcase
    filter(({ invoice_date }) =>
      // eslint-disable-next-line camelcase
      typeof invoice_date === 'string'
        ? invoice_date.match(/(^(\d{4})-(\d{2})-(\d{2}))/)
        : true,
    ),
    groupBy((row) => dateString(atFirstOfMonth(toDate(row.invoice_date)))),
    Object.entries,
    (
      groupedEntries: any,
    ): $ReadOnlyArray<[string, $ReadOnlyArray<OrderHistoryRow>]> =>
      groupedEntries,
    map(([key, value]) => [toDate(key), value]),
    (entries) => new Map(entries),
  )(rows);

export const groupedRowTotalByField = (
  groupedRows: $ReadOnlyArray<OrderHistoryRow>,
  field: 'quantity' | 'amount',
): number => Math.round(sumBy((row) => row[field], Array.from(groupedRows)));

export const groupedRowPercentByField = (
  allRows: $ReadOnlyArray<OrderHistoryRow>,
  groupedRows: $ReadOnlyArray<OrderHistoryRow>,
  field: 'quantity' | 'amount',
): number | null => {
  const allRowsTotal = sumBy((row) => row[field], Array.from(allRows));
  const groupedRowsTotal = sumBy((row) => row[field], Array.from(groupedRows));
  if (groupedRowsTotal === 0 && allRowsTotal === 0) return null;
  if (groupedRowsTotal === 0) return 0;
  return Math.round((groupedRowsTotal * 100) / allRowsTotal);
};

export type Column = $ReadOnly<{|
  label?: string,
  field?: string,
  numeric?: boolean,
  hideByDefault?: boolean,
  formatter?: (any, any, any) => string,
  unselectable?: boolean,
  valueType?: string,
  sortable?: boolean,
  url?: boolean,
|}>;

export type Columns = $ReadOnly<{|
  [key: string]: null | Column,
|}>;

const updateGroupingFacets = ({
  groupingColumns,
  groupByStr,
}: {
  groupingColumns: Columns,
  groupByStr: string,
}): $ReadOnlyArray<{| label: string, value: string |}> =>
  Object.entries(groupingColumns).map(([key, value]) => ({
    // $FlowFixMe[incompatible-type]
    label: `${groupByStr} ${value?.label || key}`,
    // $FlowFixMe[incompatible-type]
    value: `${value?.field || key}:.group`,
  }));
const updateColumns = ({
  query,
  columns,
}: {
  query: $ReadOnly<{| [key: string]: string | $ReadOnlyArray<string> |}>,
  columns: $ReadOnly<{|
    [key: string]: Column,
  |}>,
}) =>
  Object.keys(query).map((key) => {
    const parts = key.split(':.');
    const field = parts[0];
    const operator = parts[1];
    const label = columns[field] ? columns[field].label || field : field;

    const operatorToName = {
      eq: 'is',
      ne: 'is not',
      match: 'matches',
      lt: '<',
      gt: '>',
      lte: '<=',
      gte: '>=',
    };

    const nextParts = operator
      ? [label, query[key], operatorToName[operator]]
      : [label, query[key]];
    const facetStr = nextParts.join(' ');

    return { label: facetStr, value: key };
  });

const updateArrayQuery = (
  query: $ReadOnly<{| [key: string]: string | $ReadOnlyArray<string> |}>,
) =>
  Object.keys(query).map((key) => {
    const parts = key.split(':.');

    const facetStringFragment = parts.join(' ');
    return (Array.isArray(query[key]) ? query[key] : []).map((value) => {
      const facetString = `${facetStringFragment} ${value}`;
      return {
        label: facetString,
        arrayKey: key,
        value,
      };
    });
  });

export const updateFacets = ({
  groupingColumns,
  groupByStr,
  columns,
  query,
}: {
  groupingColumns: Columns,
  groupByStr: string,
  columns: $ReadOnly<{|
    [key: string]: Column,
  |}>,
  query: $ReadOnly<{| [key: string]: string | $ReadOnlyArray<string> |}>,
}): $ReadOnlyArray<any> => [
  ...updateGroupingFacets({
    groupingColumns,
    groupByStr,
  }),
  ...updateColumns({ query, columns }),
  ...updateArrayQuery(query),
];

export const externalUrl = (pattern?: string, value?: string): string =>
  pattern ? pattern.replace(/<(.*?)>/g, () => value || '') : '';

// Skillet allows a query including up to 500 customers.
const customerSelectionThreshold = 500;
export const isMaxCustomersExceeded = (user: User): boolean =>
  customers(user).length > customerSelectionThreshold;
